import React, { useCallback, useMemo } from 'react'
import { Routes, Route, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import DashboardSidebar from './DashboardSidebar'
import AppShell from '../race-tracker/components/layout/AppShell'
import CiDashboard from '../race-tracker/pages/Dashboard'
import ListView from '../race-tracker/pages/ListView'
import Analytics from '../race-tracker/pages/Analytics'
import IntelSweep from '../race-tracker/pages/IntelSweep'
import ImportPage from '../race-tracker/pages/ImportPage'
import CiSettings from '../race-tracker/pages/Settings'
import PageNotFound from '../race-tracker/lib/PageNotFound'
import { getMainSidebarActiveNavId, navigateFromMainSidebarItem } from '../lib/mainShellNav'
import { useAuth } from '../lib/AuthContext'
import { canAccessAnyAdminArea, getFirstAccessibleAdminPath } from '../lib/dashboardNav'
import 'react-day-picker/dist/style.css'

/**
 * SMG Client Management + Competitive Intelligence: shared Edison sidebar, nested CI routes.
 */
export default function RaceTrackerShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccessPage } = useAuth()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeItemId = useMemo(
    () => getMainSidebarActiveNavId(location.pathname, tabParam),
    [location.pathname, tabParam]
  )

  const showAdminPanelMenuItem = useMemo(() => canAccessAnyAdminArea(canAccessPage), [canAccessPage])

  const handleAdminPanel = useCallback(() => {
    const path = getFirstAccessibleAdminPath(canAccessPage)
    if (path) navigate(path)
  }, [canAccessPage, navigate])

  const handleSidebarNav = useCallback(
    (id) => {
      if (id === 'race-tracker') {
        navigate('/race-tracker')
        return
      }
      navigateFromMainSidebarItem(id, navigate)
    },
    [navigate]
  )

  return (
    <div
      className="rt-app"
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-body)',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <DashboardSidebar
        variant="main"
        activeItemId={activeItemId}
        onItemClick={handleSidebarNav}
        showQuickAddFooter={false}
        quickAddFooter={null}
        showAdminPanelMenuItem={showAdminPanelMenuItem}
        onAdminPanel={handleAdminPanel}
      />
      <div
        style={{
          marginLeft: 'var(--sidebar-width)',
          flex: 1,
          minHeight: '100vh',
          width: '100%',
          minWidth: 0,
          transition: 'margin-left 0.2s ease',
        }}
      >
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<CiDashboard />} />
            <Route path="list" element={<ListView />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="intel-sweep" element={<IntelSweep />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="settings" element={<CiSettings />} />
          </Route>
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </div>
    </div>
  )
}
